import { z } from 'zod';

/**
 * Liste partagée des opérateurs proposés au prospect Telco — utilisée à
 * la fois pour le champ "Opérateur fibre actuel" et "Opérateur mobile
 * actuel" (mêmes choix). Centralisée ici pour ne pas dupliquer côté UI.
 *
 * Valeurs alignées sur les libellés qui étaient déjà dans `TelcoStep.tsx`
 * (cf. PR /apps/web/.../wizard/TelcoStep.tsx) — on conserve l'orthographe
 * exacte pour ne pas casser les leads historiques dont specificData
 * stocke encore "SFR", "Orange", "Bouygues Telecom"…
 *
 * `Autre` reste un escape hatch ; pour le futur, si la liste évolue
 * (B&You, Sosh fibre, RED, etc.), c'est ici qu'on ajoute la valeur ET
 * dans le mapping logo côté front (`OPERATOR_LOGOS`).
 */
export const TELCO_OPERATORS = [
  'SFR',
  'Orange',
  'Sosh',
  'Bouygues Telecom',
  'Free',
  'Autre',
] as const;

export type TelcoOperator = (typeof TELCO_OPERATORS)[number];
export const TelcoOperatorSchema = z.enum(TELCO_OPERATORS);

/**
 * Produits Telco activables côté wizard. Un lead peut indiquer
 * `['fibre']`, `['mobile']`, ou `['fibre', 'mobile']`. La présence d'un
 * produit drive l'affichage conditionnel du champ opérateur correspondant
 * dans le wizard, et le scope de routage côté serveur.
 */
export const TelcoProduct = {
  fibre: 'fibre',
  mobile: 'mobile',
} as const;
export type TelcoProduct = (typeof TelcoProduct)[keyof typeof TelcoProduct];
export const TelcoProductSchema = z.nativeEnum(TelcoProduct);

/**
 * Scope de routage qu'un `DeliveryPartner` Telco peut servir.
 *
 *  - `fibre_only`        : ne prend que les fiches avec products=['fibre']
 *  - `mobile_only`       : ne prend que les fiches avec products=['mobile']
 *  - `fibre_and_mobile`  : ne prend que les fiches avec les deux produits
 *  - `null` (= wildcard) : sert TOUS les scopes Telco. C'est le défaut au
 *    moment de l'introduction de la feature — Club Conso reste wildcard
 *    et continue de tout recevoir comme avant.
 *
 * Résolution côté `DeliveryAssignmentService` :
 *   1. Match exact (scope = combinaison produits) → tirage pondéré
 *   2. Fallback wildcards (scope=null) → tirage pondéré
 *
 * Permet d'introduire un Armatis `fibre_only` plus tard sans toucher au
 * code ni recréer Club Conso : Armatis prendra automatiquement les fibre
 * seules via le match exact, Club Conso garde le reste via le wildcard.
 */
export const TelcoProductScope = {
  fibre_only: 'fibre_only',
  mobile_only: 'mobile_only',
  fibre_and_mobile: 'fibre_and_mobile',
} as const;
export type TelcoProductScope =
  (typeof TelcoProductScope)[keyof typeof TelcoProductScope];
export const TelcoProductScopeSchema = z.nativeEnum(TelcoProductScope);

/** Longueur max du numéro de commande Telco saisi par le vendeur. */
export const TELCO_ORDER_NUMBER_MAX = 64;

/**
 * Bloc `specificData.telco` standardisé pour les leads de la verticale
 * Telco. Stocké JSON dans la colonne `lead.specific_data`. Refine pour
 * garantir que `operateur_actuel` est présent quand le produit fibre est
 * choisi, idem pour mobile — évite les inserts incohérents.
 */
export const TelcoSpecificDataSchema = z
  .object({
    products: z.array(TelcoProductSchema).min(1),
    /** Opérateur fibre actuel du prospect — requis si products inclut 'fibre'. */
    operateur_actuel: TelcoOperatorSchema.optional(),
    /** Opérateur mobile actuel du prospect — requis si products inclut 'mobile'. */
    equipement_actuel: TelcoOperatorSchema.optional(),
    /**
     * Numéro de commande de la box vendue — facultatif, proposé seulement sur
     * les réseaux `Partner.telcoOrderNumber`. Transmis au Lab.
     */
    numero_commande: z.string().trim().max(TELCO_ORDER_NUMBER_MAX).optional(),
  })
  .refine(
    (data) => !data.products.includes('fibre') || data.operateur_actuel != null,
    {
      path: ['operateur_actuel'],
      message: 'operateur_actuel est requis quand fibre est sélectionné',
    },
  )
  .refine(
    (data) => !data.products.includes('mobile') || data.equipement_actuel != null,
    {
      path: ['equipement_actuel'],
      message: 'equipement_actuel est requis quand mobile est sélectionné',
    },
  );
export type TelcoSpecificData = z.infer<typeof TelcoSpecificDataSchema>;

/**
 * Détermine quel scope un combo de products doit matcher côté routage.
 * Centralise la règle métier pour éviter qu'elle dérive entre l'API et
 * d'éventuels consumers (tests, scripts).
 */
export function scopeForProducts(products: readonly TelcoProduct[]): TelcoProductScope {
  const hasFibre = products.includes('fibre');
  const hasMobile = products.includes('mobile');
  if (hasFibre && hasMobile) return 'fibre_and_mobile';
  if (hasMobile) return 'mobile_only';
  return 'fibre_only';
}

/**
 * Statut de facturation d'une vente TMK (source `zaf_tmk_live`, champ
 * `sales[].statusBilling`). `pending` = pas encore de statut renvoyé par
 * l'opérateur (null côté TMK). Sert à distinguer une vente confirmée (`OK`)
 * d'une rejetée (`KO`) / en attente (`WAIT`) dans l'affichage Doorly.
 */
export const TELCO_SALE_BILLING_STATUSES = ['OK', 'KO', 'WAIT', 'pending'] as const;
export type TelcoSaleBillingStatus = (typeof TELCO_SALE_BILLING_STATUSES)[number];
export const TelcoSaleBillingStatusSchema = z.enum(TELCO_SALE_BILLING_STATUSES);

/**
 * Libellés FR — source unique (fiche, liste, exports). `pending` = TMK n'a
 * reçu AUCUN retour de l'opérateur (ni vente ni facturation) : « Sans retour
 * opérateur », distinct de `WAIT` (l'opérateur a répondu « en attente »).
 * Était « En cours », lu comme « la ligne n'est pas encore activée » alors
 * que l'activation existe parfois côté opérateur sans être rattachée dans
 * TMK (retour de Cyrille, 08/10/2026).
 */
export const TELCO_SALE_BILLING_STATUS_LABEL_FR: Record<TelcoSaleBillingStatus, string> = {
  OK: 'Confirmée',
  KO: 'Rejetée',
  WAIT: 'En attente',
  pending: 'Sans retour opérateur',
};

/**
 * Canal d'une vente TMK réconciliée : fibre (= univers `fixe` côté TMK,
 * box/fibre) ou mobile. `inconnu` couvre les rares ventes dont l'univers
 * TMK est absent ET dont l'offre n'a pas pu être classée par heuristique.
 */
export const TELCO_SALE_CANALS = ['fibre', 'mobile', 'inconnu'] as const;
export type TelcoSaleCanal = (typeof TELCO_SALE_CANALS)[number];
export const TelcoSaleCanalSchema = z.enum(TELCO_SALE_CANALS);

/**
 * Une vente TMK rattachée à un lead Doorly (verticale Telco, livrée au
 * Lab). Réconciliation par téléphone E.164 + fenêtre temporelle : il
 * n'existe PAS d'id déterministe commun entre Doorly et TMK
 * (`collectUserId`/`hipto_uid` n'est pas propagé jusqu'à BigQuery — cf.
 * DeliveryService). Un lead peut porter PLUSIEURS ventes (ex. 1 box fibre
 * + 3 lignes mobile). `tmkSaleId` (= `sales[]._id` Mongo) est la clé
 * d'idempotence de l'upsert. `tmkDataId` (= `externalInfo.body.data_id`)
 * regroupe les lignes issues d'un même lead entrant zaf.
 */
export const LeadTelcoSaleSchema = z.object({
  canal: TelcoSaleCanalSchema,
  offerName: z.string().nullable(),
  operator: z.string().nullable(),
  billingStatus: TelcoSaleBillingStatusSchema,
  soldAt: z.string().nullable(),
  refCommand: z.string().nullable(),
  tmkSaleId: z.string(),
  tmkDataId: z.string().nullable(),
});
export type LeadTelcoSale = z.infer<typeof LeadTelcoSaleSchema>;

/**
 * Synthèse fibre/mobile portée par le lead pour l'affichage liste/détail.
 * `enrichedAt` = date du dernier passage de réconciliation TMK (null =
 * jamais tenté). Les compteurs sont dérivés de `sales`.
 */
export const LeadTelcoBreakdownSchema = z.object({
  fibreCount: z.number().int().nonnegative(),
  mobileCount: z.number().int().nonnegative(),
  enrichedAt: z.string().nullable(),
  sales: z.array(LeadTelcoSaleSchema),
});
export type LeadTelcoBreakdown = z.infer<typeof LeadTelcoBreakdownSchema>;
